import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HiChevronDown, HiArrowRightOnRectangle, HiBell, HiInbox } from 'react-icons/hi2';
import mainLogo from '../../../components/assets/images/bdih_logo.png';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, firestore } from '../../../database/firebase';
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore';
import { useAuth } from '../../../context/AuthContext';
import LogoutConfirmation from '../../../components/ui/LogoutConfirmation';

const dropdownVariants = {
  hidden: { opacity: 0, y: -10, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: 'spring', stiffness: 200, damping: 18, duration: 0.22 },
  },
  exit: { opacity: 0, y: -10, scale: 0.98, transition: { duration: 0.15 } },
};

const CoachesHeader = () => {
  const { logout } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const dropdownRef = useRef(null);
  const notificationsRef = useRef(null);
  const inboxRef = useRef(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async currentUser => {
      setLoading(true);
      if (currentUser) {
        setUser(currentUser);
        try {
          // Check if user is a delegated user - use parent coach ID
          const delegatedUserData = localStorage.getItem('delegatedUser');
          let effectiveUid = currentUser.uid;

          if (delegatedUserData) {
            try {
              const parsed = JSON.parse(delegatedUserData);
              effectiveUid = parsed.coachId || currentUser.uid;
            } catch (e) {
              // Invalid JSON, use current user UID
            }
          }

          const userDocRef = doc(firestore, 'coaches', effectiveUid);
          const userDocSnap = await getDoc(userDocRef);
          if (userDocSnap.exists()) {
            setUserProfile(userDocSnap.data());
          } else {
            setUserProfile(null);
          }
        } catch (error) {
          setUserProfile(null);
        }
      } else {
        setUser(null);
        setUserProfile(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Task notifications - Coaches see tasks assigned to them
  useEffect(() => {
    if (!user?.email) return;

    // Get effective email (parent coach email if delegated user)
    const delegatedUserData = localStorage.getItem('delegatedUser');
    let effectiveEmail = user.email;

    if (delegatedUserData) {
      try {
        const parsed = JSON.parse(delegatedUserData);
        effectiveEmail = parsed.coachEmail || user.email;
      } catch (e) {
        // Invalid JSON, use user email
      }
    }

    const tasksQuery = query(
      collection(firestore, 'tasks'),
      where('assignedToEmail', '==', effectiveEmail)
    );

    const unsubscribeTasks = onSnapshot(
      tasksQuery,
      snapshot => {
        snapshot.docChanges().forEach(change => {
          if (change.type === 'added') {
            const taskData = change.doc.data();
            // Only notify for non-completed tasks
            if (taskData.status !== 'completed') {
              setNotifications(prev => {
                if (prev.some(notification => notification.id === `task-${change.doc.id}`))
                  return prev;
                return [
                  ...prev,
                  {
                    id: `task-${change.doc.id}`,
                    type: 'task',
                    message: `New task assigned: ${taskData.title || 'Untitled Task'}`,
                    timestamp: taskData.createdAt || serverTimestamp(),
                    read: false,
                    taskId: change.doc.id,
                  },
                ];
              });
            }
          }
        });
      },
      error => {
        console.error('Task notification listener error:', error);
      }
    );

    return () => unsubscribeTasks();
  }, [user?.email]);

  useEffect(() => {
    setUnreadCount(notifications.filter(n => !n.read).length);
  }, [notifications]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setNotificationsOpen(false);
      }
      if (inboxRef.current && !inboxRef.current.contains(event.target)) {
        setInboxOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = async () => {
    setShowLogoutConfirm(false);
    await logout();
  };

  if (loading) {
    return (
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="sticky top-0 z-40 flex w-full items-center justify-between border-b border-gray-200 bg-white/80 px-6 py-4 backdrop-blur-xl"
      >
        <div className="flex items-center gap-4">
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
          >
            <h1 className="text-xl font-bold text-black">
              BDIH <span className="ml-2 text-xs font-normal text-gray-500">Coach</span>
            </h1>
          </motion.div>
        </div>
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 animate-pulse rounded-full bg-gray-200"></div>
          <div className="hidden flex-col gap-1 sm:flex">
            <div className="h-3 w-20 animate-pulse rounded bg-gray-200"></div>
            <div className="h-2 w-16 animate-pulse rounded bg-gray-200"></div>
          </div>
        </div>
      </motion.header>
    );
  }

  if (!user) return null;

  const displayName =
    userProfile?.firstname && userProfile?.lastname
      ? `${userProfile.firstname} ${userProfile.lastname}`
      : user.displayName || 'Coach';
  const firstName = userProfile?.firstname || user.displayName?.split(' ')[0] || 'Coach';
  const lastName = userProfile?.lastname || user.displayName?.split(' ')[1] || '';
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

  return (
    <motion.header
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="sticky top-0 z-40 flex w-full items-center justify-between border-b border-gray-200 bg-white/80 px-6 py-4 backdrop-blur-xl"
    >
      <div className="flex items-center gap-4">
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
        >
          <h1 className="text-xl font-bold text-black">
            <span className="ml-2 text-xs font-normal text-gray-500">Coach</span>
          </h1>
        </motion.div>
      </div>
      <div className="flex items-center gap-4">
        <div className="relative" ref={inboxRef}>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setInboxOpen(!inboxOpen)}
            className="relative rounded-xl p-2 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700"
          >
            <HiInbox className="h-5 w-5" />
            {unreadMessages > 0 && (
              <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-blue-600 text-xs text-white">
                {unreadMessages}
              </span>
            )}
          </motion.button>
          <AnimatePresence>
            {inboxOpen && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -10 }}
                transition={{ duration: 0.15, ease: 'easeOut' }}
                className="absolute right-0 mt-2 w-80 rounded-2xl bg-white shadow-xl ring-1 ring-black/5 backdrop-blur-xl"
              >
                <div className="border-b border-gray-100 px-4 py-3">
                  <h3 className="text-sm font-semibold text-black">Inbox</h3>
                </div>
                <div className="max-h-96 overflow-y-auto py-2">
                  {messages.length > 0 ? (
                    messages.map(message => (
                      <motion.div
                        key={message.id}
                        whileHover={{ backgroundColor: 'rgb(248 250 252)' }}
                        className={`flex items-start gap-3 px-4 py-3 ${
                          !message.read ? 'bg-gray-50' : ''
                        }`}
                      >
                        <div className="flex-1">
                          <p className="text-sm text-black">{message.subject}</p>
                          <p className="mt-1 text-xs text-gray-500">
                            {new Date(message.timestamp).toLocaleString()}
                          </p>
                        </div>
                      </motion.div>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-center text-sm text-gray-500">No messages</div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="relative" ref={notificationsRef}>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setNotificationsOpen(!notificationsOpen)}
            className="relative rounded-xl p-2 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700"
          >
            <HiBell className="h-5 w-5" />
            {unreadCount > 0 && (
              <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-blue-600 text-xs text-white">
                {unreadCount}
              </span>
            )}
          </motion.button>
          <AnimatePresence>
            {notificationsOpen && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -10 }}
                transition={{ duration: 0.15, ease: 'easeOut' }}
                className="absolute right-0 mt-2 w-80 rounded-2xl bg-white shadow-xl ring-1 ring-black/5 backdrop-blur-xl"
              >
                <div className="border-b border-gray-100 px-4 py-3">
                  <h3 className="text-sm font-semibold text-black">Notifications</h3>
                </div>
                <div className="max-h-96 overflow-y-auto py-2">
                  {notifications.length > 0 ? (
                    notifications.map(notification => (
                      <motion.div
                        key={notification.id}
                        whileHover={{ backgroundColor: 'rgb(248 250 252)' }}
                        className={`flex items-start gap-3 px-4 py-3 ${
                          !notification.read ? 'bg-gray-50' : ''
                        }`}
                      >
                        <div className="flex-1">
                          <p className="text-sm text-black">{notification.message}</p>
                          <p className="mt-1 text-xs text-gray-500">
                            {notification.timestamp?.toDate
                              ? new Date(notification.timestamp.toDate()).toLocaleString()
                              : notification.timestamp
                                ? new Date(notification.timestamp).toLocaleString()
                                : 'Just now'}
                          </p>
                        </div>
                      </motion.div>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-center text-sm text-gray-500">
                      No notifications
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="relative" ref={dropdownRef}>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex items-center gap-3 rounded-xl px-3 py-2 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            onClick={() => setDropdownOpen(open => !open)}
          >
            <div className="relative">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-600 text-sm font-semibold text-white shadow-lg">
                {initials}
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white bg-green-500"></div>
            </div>
            <div className="hidden flex-col items-start text-left sm:flex">
              <span className="text-sm font-semibold text-black">{firstName}</span>
              <span className="text-xs text-gray-500">{user.email}</span>
            </div>
            <motion.div animate={{ rotate: dropdownOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
              <HiChevronDown className="h-4 w-4 text-gray-400" />
            </motion.div>
          </motion.button>
          <AnimatePresence>
            {dropdownOpen && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -10 }}
                transition={{ duration: 0.15, ease: 'easeOut' }}
                className="absolute right-0 mt-2 w-64 rounded-2xl bg-white shadow-xl ring-1 ring-black/5 backdrop-blur-xl"
              >
                <div className="border-b border-gray-100 px-4 py-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-600 text-sm font-semibold text-white shadow-lg">
                      {initials}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold text-black">{displayName}</div>
                      <div className="truncate text-xs text-gray-500">{user.email}</div>
                    </div>
                  </div>
                </div>
                <div className="py-2">
                  <motion.button
                    whileHover={{ backgroundColor: 'rgb(248 250 252)' }}
                    onClick={handleLogout}
                    className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm text-gray-600 transition-colors"
                  >
                    <HiArrowRightOnRectangle className="h-4 w-4" />
                    <span>Sign out</span>
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      <LogoutConfirmation
        isOpen={showLogoutConfirm}
        onConfirm={confirmLogout}
        onCancel={() => setShowLogoutConfirm(false)}
      />
    </motion.header>
  );
};

export default CoachesHeader;
